'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/ui/alert-dialog';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/ui/drawer';
import { Button } from '@/ui/button';
import { cn } from '@/helpers/utils';

// Same shape as ResponsiveDialog (src/ui/responsive-dialog.jsx) — a centered
// AlertDialog on desktop, a bottom Drawer on mobile — for confirm()'s single
// shared instance (src/providers/confirm-provider.jsx), which otherwise
// showed as a centered dialog on every breakpoint. Read once per instance and
// shared via context for the same reason ResponsiveDialog does: Root and
// Content picking mismatched mobile values a render apart would otherwise
// briefly pair AlertDialog with DrawerContent or vice versa.
const ResponsiveAlertDialogMobileContext = createContext(false);
const useResponsiveAlertDialogMobile = () => {
  return useContext(ResponsiveAlertDialogMobileContext);
};
export const ResponsiveAlertDialog = t0 => {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const isMobile = useIsMobile();
  const Root = isMobile ? Drawer : AlertDialog;
  let t1;
  if ($[3] !== Root || $[4] !== children || $[5] !== props) {
    t1 = <Root data-slot="responsive-alert-dialog" {...props}>{children}</Root>;
    $[3] = Root;
    $[4] = children;
    $[5] = props;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== isMobile || $[8] !== t1) {
    t2 = <ResponsiveAlertDialogMobileContext.Provider value={isMobile}>{t1}</ResponsiveAlertDialogMobileContext.Provider>;
    $[7] = isMobile;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const ResponsiveAlertDialogContent = t0 => {
  const $ = _c(6);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const isMobile = useResponsiveAlertDialogMobile();
  if (isMobile) {
    let t1;
    if ($[2] !== props) {
      t1 = <DrawerContent data-slot="responsive-alert-dialog-content" {...props} />;
      $[2] = props;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== props) {
    t1 = <AlertDialogContent data-slot="responsive-alert-dialog-content" {...props} />;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const ResponsiveAlertDialogHeader = t0 => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const isMobile = useResponsiveAlertDialogMobile();
  const Header = isMobile ? DrawerHeader : AlertDialogHeader;
  let t1;
  if ($[2] !== Header || $[3] !== props) {
    t1 = <Header data-slot="responsive-alert-dialog-header" {...props} />;
    $[2] = Header;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const ResponsiveAlertDialogFooter = t0 => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const isMobile = useResponsiveAlertDialogMobile();
  const Footer = isMobile ? DrawerFooter : AlertDialogFooter;
  let t1;
  if ($[2] !== Footer || $[3] !== props) {
    t1 = <Footer data-slot="responsive-alert-dialog-footer" {...props} />;
    $[2] = Footer;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const ResponsiveAlertDialogTitle = t0 => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const isMobile = useResponsiveAlertDialogMobile();
  const Title = isMobile ? DrawerTitle : AlertDialogTitle;
  let t1;
  if ($[2] !== Title || $[3] !== props) {
    t1 = <Title data-slot="responsive-alert-dialog-title" {...props} />;
    $[2] = Title;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const ResponsiveAlertDialogDescription = t0 => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const isMobile = useResponsiveAlertDialogMobile();
  const Description = isMobile ? DrawerDescription : AlertDialogDescription;
  let t1;
  if ($[2] !== Description || $[3] !== props) {
    t1 = <Description data-slot="responsive-alert-dialog-description" {...props} />;
    $[2] = Description;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

// Wraps any content between Header and Footer (confirm-provider.jsx's
// type-to-confirm Field, currently the only user) — AlertDialogContent
// already pads its whole popup (p-6, so every direct child inherits side
// padding for free), but DrawerContent only pads Header/Footer individually
// (each p-4 on their own box), leaving a plain child sat between them with no
// side padding at all on mobile.
export const ResponsiveAlertDialogBody = t0 => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const isMobile = useResponsiveAlertDialogMobile();
  const t1 = isMobile && "px-4";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn(t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2) {
    t3 = <div data-slot="responsive-alert-dialog-body" className={t2} {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

// Always a plain Button, both breakpoints — confirm-provider.jsx calls
// settle(true) itself on click (and keeps it disabled during the
// type-to-confirm guard), so there's no primitive auto-close to swap; Drawer
// has no dedicated "action" close variant to reach for here anyway.
export const ResponsiveAlertDialogAction = t0 => {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <Button data-slot="responsive-alert-dialog-action" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const ResponsiveAlertDialogCancel = t0 => {
  const $ = _c(14);
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
    $[3] = t2;
  } else {
    props = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  const variant = t1 === undefined ? "outline" : t1;
  const size = t2 === undefined ? "default" : t2;
  const isMobile = useResponsiveAlertDialogMobile();
  if (isMobile) {
    let t3;
    if ($[4] !== size || $[5] !== variant) {
      t3 = <Button variant={variant} size={size} />;
      $[4] = size;
      $[5] = variant;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== props || $[8] !== t3) {
      t4 = <DrawerClose data-slot="responsive-alert-dialog-cancel" render={t3} {...props} />;
      $[7] = props;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t3;
  if ($[10] !== props || $[11] !== size || $[12] !== variant) {
    t3 = <AlertDialogCancel data-slot="responsive-alert-dialog-cancel" variant={variant} size={size} {...props} />;
    $[10] = props;
    $[11] = size;
    $[12] = variant;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};